<script setup lang="ts">
import { useCategoryTreeQuery } from "@/queries/categories";

import Drawer from "primevue/drawer";
import Dock from "primevue/dock";
import CustomHierarchicalMenu from "@/features/search/components/hierarchical/CustomHierarchicalMenu.vue";
import Search from "@/features/search/ui/Search.vue";

import CustomSvgIcons from "@/components/HelpersComponents/CustomSvgIcons.vue";

import { computed, ref, watch } from "vue";
import { DOCKCustomStyles } from "@/features/bottom-app-bar/lib/DockCustomStyles";

// Category navigation comes from the ML category tree, not a bundled list.
const categoryTreeQuery = useCategoryTreeQuery();
const fullMenu = computed(() => categoryTreeQuery.data.value ?? []);
import { useRoute } from "vue-router";
import router from "@/router";
import { useTokenStore, useAuthModal } from "@/features/auth/store";
import { useSharedStateForComponents } from "@/features/search/store";
import { useChatStore } from "@/aiAsisstant/stores/chat";

interface DockItem {
  label: string;
  icon: string;
  selected?: boolean;
  buttonCustomClass?: string;
  command?: () => void;
}

const route = useRoute();
const tokenStore = useTokenStore();
const authModal = useAuthModal();
const sharedComponentState = useSharedStateForComponents();
const chatStore = useChatStore();

const focusState = ref(false);

const hierarchical = ref<InstanceType<typeof CustomHierarchicalMenu> | null>(null);
const testRef = ref<InstanceType<typeof Search> | null>(null);

const items = ref<DockItem[]>([
  {
    label: "Home",
    icon: "home",
    selected: false,
    buttonCustomClass: "custom",
    command: () => {
      router.push({ path: "/" });
    },
  },
  {
    label: "Search",
    icon: "search",
    selected: false,
    buttonCustomClass: "custom",
    command: () => {
      items.value[1].selected = !items.value[1].selected;
    },
  },
  {
    label: "Center",
    icon: "scope",
    buttonCustomClass: "centerIconClass",
    command: () => {
      chatStore.openChat();
    },
  },
  {
    label: "Mine",
    icon: "bookmark",
    selected: false,
    buttonCustomClass: "custom",
  },
  {
    label: "Profile",
    icon: "profile",
    command: () => {
      if (!tokenStore.accessToken) {
        authModal.showAuthModal("signInSignUpWrapper");
      } else {
        router.push({ path: "/" });
      }
    },
    selected: false,
    buttonCustomClass: "custom",
  },
]);

const onDockItemClick = (event: MouseEvent, item: DockItem) => {
  if (item.command) {
    item.command();
  }

  if (event) event?.preventDefault();
};

watch(
  () => sharedComponentState.isAutofocusForSearchActive,
  (value, oldValue, onCleanup) => {
    if (
      value &&
      typeof items.value[1].command !== "undefined" &&
      !items.value[1].selected
    ) {
      items.value[1].command();
      focusState.value = true;
    }
  }
);

watch(route, () => {
  if (route.matched[0].name === "home") {
    items.value[0].selected = true;
  } else {
    items.value[0].selected = false;
  }
});
</script>

<template>
  <Dock :model="items" :position="'bottom'" :pt="DOCKCustomStyles">
    <template #itemicon="{ item }">
      <div
        class="mobile-menu-buttons-item"
        :class="`${item.buttonCustomClass} ${item.selected ? 'selected' : ''}`"
        @click="onDockItemClick($event, item)"
      >
        <custom-svg-icons :name="item.icon" :size="22" />
        <span v-if="item.label !== 'Center'">{{ item.label }}</span>

        <Drawer
          v-if="item.label === 'Search'"
          v-model:visible="items[1].selected"
          @after-hide="sharedComponentState.isAutofocusForSearchActive = false"
          header="Drawer"
          :pt="{ root: { style: { width: '100%' } } }"
        >
          <div v-if="!hierarchical?.currentCategory" class="rel">
            <Search
              ref="testRef"
              :collapsed="true"
              :custom-open-with-bottom-app-bar-active="true"
              v-model:model-value="focusState"
            />
          </div>
          <custom-hierarchical-menu
            v-if="!focusState && fullMenu.length > 0"
            :full-menu="fullMenu"
            ref="hierarchical"
          />
        </Drawer>
      </div>
    </template>
  </Dock>
</template>

<style scoped>
:deep(.search__wrapper) {
  position: relative;
}

.mobile-menu-buttons-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px;
  border-radius: 50%;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  font-size: 10px;
  font-weight: 500;
  width: 100%;
  height: 100%;
}

.centerIconClass {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(216, 229, 173, 1);
  color: rgba(216, 229, 173, 1);
  width: 100%;
  height: 100%;
  background: rgba(110, 133, 203, 0.2);
}

.centerIconClass svg {
  color: var(--yellow-100);
}

.selected svg {
  color: white;
}

.mobile-menu-buttons-item {
}

.selected {
  color: var(--white-100);
  background: var(--active-100);
}

.selected svg {
  color: var(--white-100);
}
</style>
